/**
 * recordServiceSide — which face (Care management or SPICE) a client on
 * BOTH services currently shows in the client record: the Notes &
 * activity composer, its heading/bands/door, the critical strip's face
 * cell, and the header's "+ Case Note" / "+ Session Note" button all
 * read this one value.
 *
 * A viewer who may switch faces opens on Care management, then keeps
 * whichever side she last picked for THIS client in sessionStorage
 * (spiceVisitPrefill.ts's try/catch discipline — private mode / quota
 * never breaks the toggle, it just isn't remembered). A SPICE-only
 * viewer (spiceOnlyViewer.ts) holds the SPICE face with no toggle: the
 * case-note composer is the care manager's door, and a stored pick never
 * moves her off SPICE.
 */

import type { ClientEngagement } from '@/lib/clientRecord';
import { isSpiceOnlyViewer } from '@/lib/spiceOnlyViewer';

export type RecordServiceSide = 'care_management' | 'spice';

function recordServiceSideKey(tenantSlug: string, clientId: string): string {
  return `record-service-side:${tenantSlug}:${clientId}`;
}

type ViewerFlags =
  | { does_spice?: boolean | null; does_care_management?: boolean | null }
  | null
  | undefined;

/** The viewer's own side: a SPICE-only viewer opens on SPICE; every other
 *  viewer (including one with neither flag, or both) opens on Care
 *  management. */
export function defaultRecordServiceSide(whoami: ViewerFlags): RecordServiceSide {
  return isSpiceOnlyViewer(whoami) ? 'spice' : 'care_management';
}

/** Whether this viewer may switch a client on both services between its
 *  two faces. A SPICE-only viewer never may: the case-note composer is
 *  the care manager's door, so her record holds the SPICE face. */
export function canSwitchRecordServiceSide(whoami: ViewerFlags): boolean {
  return !isSpiceOnlyViewer(whoami);
}

/** The side this viewer last picked for this client, or null when
 *  nothing is stored yet (a fresh tab, private mode, or a read error). */
export function readRecordServiceSide(tenantSlug: string, clientId: string): RecordServiceSide | null {
  try {
    const raw = window.sessionStorage.getItem(recordServiceSideKey(tenantSlug, clientId));
    return raw === 'spice' || raw === 'care_management' ? raw : null;
  } catch {
    return null;
  }
}

export function writeRecordServiceSide(
  tenantSlug: string,
  clientId: string,
  side: RecordServiceSide,
): void {
  try {
    window.sessionStorage.setItem(recordServiceSideKey(tenantSlug, clientId), side);
  } catch {
    // sessionStorage unavailable (private mode / quota) — the toggle
    // still flips for this render; it just won't be remembered.
  }
}

/** The side a client on both services shows this viewer: the side she
 *  last picked for this client when she may switch, else her own side.
 *  A stored pick never moves a SPICE-only viewer off SPICE. */
export function resolveRecordServiceSide(
  whoami: ViewerFlags,
  tenantSlug: string,
  clientId: string,
): RecordServiceSide {
  const picked = canSwitchRecordServiceSide(whoami)
    ? readRecordServiceSide(tenantSlug, clientId)
    : null;
  return picked ?? defaultRecordServiceSide(whoami);
}

/** The face a client record shows. A client on both services shows the
 *  side the viewer picked (the toggle); a client on one service has that
 *  one face. The header's note button, the Notes & activity composer and
 *  the critical strip's face cell all read this one value. */
export function recordFaceFor(engagement: ClientEngagement, side: RecordServiceSide): RecordServiceSide {
  return engagement === 'both' ? side : engagement;
}
